var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { SheetAlignmentEnum } from '@inter-ikea-kompis/enums';
import { FontStyle } from '@inter-ikea-kompis/themes';
import { DexfSettingsUtility } from '@inter-ikea-kompis/utilities';
import { KeyboardInjectorFinder } from '@inter-ikea-kompis/component-keyboard';
import { OpenDesignCodeStateEnum } from '@inter-ikea-kompis/component-open-design-code';
import { Button as SkapaButton, IconButton as SkapaIconButton } from '@ingka/button-webc/passive.js';
import { Text } from '@ingka/common-styles-webc';
import { Icon as SkapaIcon } from '@ingka/icon-webc/passive.js';
import { InlineMessage as SkapaInlineMessage } from '@ingka/inline-message-webc/passive.js';
import { ModalFooter as SkapaModalFooter, ModalHeader as SkapaModalHeader, Prompt as SkapaPrompt, Sheet as SkapaSheet } from '@ingka/modal-webc/passive.js';
import OpenDesignModalEnum from '../../enums/OpenDesignModalEnum.js';
import OpenDesignPromptEnum from '../../enums/OpenDesignPromptEnum.js';
import OpenDesignStateEnum from '../../enums/OpenDesignStateEnum.js';
import OpenDesignTabEnum from '../../enums/OpenDesignTabEnum.js';
import OpenDesignVariantEnum from '../../enums/OpenDesignVariantEnum.js';
import OpenDesignCard from '../open-design-card/OpenDesignCard.js';
import OpenDesignStyle from './OpenDesignStyle.js';
/**
 * Component used for letting the user open or import an existing design, either
 * using a VPC code or from their account.
 *
 * @event modalClose Modal close.
 * @event promptClose Prompt close.
 * @event logIn Triggered when the log in button is clicked.
 * @event openSavedDesign Triggered when the open/import button is clicked when
 * a saved design is selected.
 * @event openDesignCode Triggered when the open/import button is clicked when a
 * design code is entered.
 * @event resetOpenDesignCodeState Conditionally triggered when a tab is
 * selected.
 * @event backButtonClick Triggered when the back button is clicked.
 * @event tabSelect Triggered when a tab is selected.
 */
export default class OpenDesign extends KompisElement {
    constructor() {
        super(...arguments);
        // Localized information. Deprecated - use "translations" and "settings" instead.
        this.localizedInformation = null;
        // The translations retrieved from translations service.
        this.translations = null;
        // The settings retrieved from settings service.
        this.settings = null;
        // Visible modal.
        this.visibleModal = null;
        // Visible prompt. This prompt can be stacked "on top of" the visible modal and should prompt a logged out user to log in.
        this.visiblePrompt = null;
        // Whether the user is logged in to IKEA or not. Always set to false when in Kiosk.
        this.loggedIn = false;
        // Whether to show tabs in the sheet. Always set to false when in Kiosk.
        this.enableTabs = false;
        // The state of open design code.
        this.openDesignCodeState = OpenDesignCodeStateEnum.default;
        // List of saved designs retrieved from save service.
        this.savedDesigns = null;
        // The state of open saved design.
        this.openSavedDesignState = OpenDesignStateEnum.default;
        // Where the sheet will open. The values are flipped in RTL, meaning "right" will open to the left and vice versa.
        this.sheetAlignment = SheetAlignmentEnum.right;
        // Whether or not to show a back button.
        this.showBackButton = false;
        // Which variant of the component to show. Currently open design (default) or import design.
        this.variant = OpenDesignVariantEnum.openDesign;
        // Message to be displayed in an inline message, e.g. "Currently only available for PAX wardrobe designs".
        this.helpMessage = null;
        // The tab that OpenDesignCard should display. Always set to OpenDesignTabEnum.designCode in Kiosk
        this.selectedTab = OpenDesignTabEnum.designCode;
        this.promptElement = null;
        this.sheetElement = null;
        this.cardElement = null;
        this.sheetHeaderElement = null;
        this.sheetFooterElement = null;
        this.dexfSettings = null;
    }
    render() {
        return html `
			<style>
				${OpenDesignStyle.getStyle({
            theme: this.theme
        })}
			</style>
		`;
    }
    async connectedCallback() {
        this.customElements.define('skapa-sheet', SkapaSheet);
        this.customElements.define('skapa-modal-footer', SkapaModalFooter);
        this.customElements.define('skapa-modal-header', SkapaModalHeader);
        this.customElements.define('skapa-prompt', SkapaPrompt);
        this.customElements.define('skapa-button', SkapaButton);
        this.customElements.define('skapa-icon-button', SkapaIconButton);
        this.customElements.define('skapa-icon-button', SkapaIconButton);
        this.customElements.define('skapa-icon', SkapaIcon);
        this.customElements.define('skapa-inline-message', SkapaInlineMessage);
        this.customElements.define('kompis-open-design-card', OpenDesignCard);
        super.connectedCallback();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.closeSheet();
    }
    shouldUpdate(changedProperties) {
        if (changedProperties.has('localizedInformation') &&
            this.localizedInformation &&
            !this.settings) {
            this.dexfSettings = DexfSettingsUtility.parseLocalizedInformation(this.localizedInformation);
        }
        if (changedProperties.has('settings') && this.settings) {
            this.dexfSettings = this.settings;
        }
        if (changedProperties.has('visibleModal')) {
            if (this.visibleModal === OpenDesignModalEnum.openDesignCard) {
                this.openSheet();
            }
            else {
                this.closeSheet();
            }
        }
        if (changedProperties.has('visiblePrompt')) {
            if (this.visiblePrompt === OpenDesignPromptEnum.loginPrompt) {
                this.openPrompt();
            }
            else {
                this.closePrompt();
            }
        }
        if (this.cardElement) {
            this.cardElement.theme = this.theme;
            this.cardElement.localizedInformation = this.localizedInformation;
            this.cardElement.translations = this.translations;
            this.cardElement.settings = this.settings;
            this.cardElement.loggedIn = this.loggedIn;
            this.cardElement.enableTabs = this.enableTabs;
            this.cardElement.selectedTab = this.selectedTab;
            this.cardElement.openDesignCodeState = this.openDesignCodeState;
            this.cardElement.openSavedDesignState = this.openSavedDesignState;
            this.cardElement.savedDesigns = this.savedDesigns;
            this.cardElement.variant = this.variant;
            this.cardElement.helpMessage = this.helpMessage;
        }
        if (changedProperties.has('showBackButton')) {
            this.recreateSheetHeader();
        }
        if (changedProperties.has('enableTabs') ||
            changedProperties.has('loggedIn') ||
            changedProperties.has('savedDesigns') ||
            changedProperties.has('selectedTab') ||
            changedProperties.has('variant')) {
            this.recreateSheetFooter();
        }
        if (changedProperties.has('openDesignCodeState')) {
            const button = this.getFooterButton();
            if (button) {
                button.loading = this.openDesignCodeState === OpenDesignCodeStateEnum.loading;
            }
        }
        return true;
    }
    getTranslations() {
        const translations = this.translations
            ? this.translations
            : this.localizedInformation?.translations;
        if (!translations) {
            throw new Error('Translations must be defined either through the "translations" or "localizedInformation" prop.');
        }
        return translations;
    }
    getSettings() {
        if (!this.dexfSettings) {
            throw new Error('Settings must be defined either through the "settings" or "localizedInformation" prop.');
        }
        return this.dexfSettings;
    }
    closeSheet() {
        if (this.sheetElement) {
            this.sheetElement.open = false;
            this.sheetElement = null;
            this.cardElement = null;
            this.sheetHeaderElement = null;
            this.sheetFooterElement = null;
        }
    }
    openSheet() {
        if (this.sheetElement) {
            return;
        }
        const sheetElement = this.createSheetElement();
        sheetElement.addEventListener('closed', () => {
            sheetElement.parentElement?.removeChild(sheetElement);
        });
        this.sheetElement = sheetElement;
        this.cardElement = sheetElement.querySelector(this.customElements.getInternalName('kompis-open-design-card'));
        this.sheetHeaderElement = sheetElement.querySelector(this.customElements.getInternalName('skapa-modal-header'));
        this.sheetFooterElement = sheetElement.querySelector(this.customElements.getInternalName('skapa-modal-footer'));
        const parent = KeyboardInjectorFinder.findKeyboardInjectorElement() || document.body;
        parent.appendChild(sheetElement);
        sheetElement.open = true;
    }
    recreateSheetHeader() {
        if (this.sheetElement) {
            if (this.sheetHeaderElement) {
                this.sheetElement.removeChild(this.sheetHeaderElement);
                this.sheetHeaderElement = null;
            }
            const header = this.createSheetHeader();
            this.sheetElement.prepend(header);
            this.sheetHeaderElement = header;
        }
    }
    createSheetElement() {
        const sheetElement = this.createSheet();
        const sheetHeaderElement = this.createSheetHeader();
        const openDesignCard = this.createCard();
        const sheetFooterElement = this.createSheetFooter();
        sheetElement.appendChild(sheetHeaderElement);
        sheetElement.appendChild(openDesignCard);
        if (sheetFooterElement) {
            sheetElement.appendChild(sheetFooterElement);
        }
        return sheetElement;
    }
    createSheet() {
        const sheetElement = this.createInternalElement('skapa-sheet');
        const ariaLabel = this.variant === OpenDesignVariantEnum.openDesign
            ? this.getTranslations().openDesignHeader
            : this.getTranslations().importDesignHeader;
        sheetElement.setAttribute('aria-label', ariaLabel);
        sheetElement.size = 'small';
        sheetElement.style.zIndex = '500';
        sheetElement.fullHeight = true;
        sheetElement.alignToStart = this.sheetAlignment === SheetAlignmentEnum.left;
        sheetElement.style.fontFamily = FontStyle.getFontFamily(this.theme);
        sheetElement.addEventListener('closerequest', () => {
            if (this.sheetElement) {
                this.dispatchEvent(new CustomEvent('modalClose', {
                    bubbles: true,
                    detail: {
                        visibleModal: null
                    }
                }));
            }
        });
        return sheetElement;
    }
    createSheetHeader() {
        const sheetHeaderElement = this.createInternalElement('skapa-modal-header');
        sheetHeaderElement.slot = 'header';
        const headerSpan = document.createElement('span');
        headerSpan.slot = 'closebutton-label';
        headerSpan.innerText = this.getTranslations().modalAriaLabelButtonClose;
        sheetHeaderElement.appendChild(headerSpan);
        if (this.showBackButton) {
            const backButton = this.createBackButton();
            sheetHeaderElement.appendChild(backButton);
            sheetHeaderElement.addEventListener('backrequest', () => {
                this.dispatchEvent(new CustomEvent('backButtonClick', {
                    bubbles: true,
                    detail: {
                        visibleModal: null
                    }
                }));
            });
        }
        return sheetHeaderElement;
    }
    createBackButton() {
        const backButton = this.createInternalElement('skapa-icon-button');
        backButton.slot = 'back-button';
        backButton.variant = 'primary';
        backButton.inverse = true;
        backButton.size = 'small';
        backButton.innerText = this.getTranslations().modalAriaLabelButtonBack;
        const icon = this.createInternalElement('skapa-icon');
        icon.slot = 'icon';
        icon.icon = 'arrow-left';
        icon.flipRTL = true;
        backButton.append(icon);
        return backButton;
    }
    createCard() {
        const openDesignCard = this.createInternalElement('kompis-open-design-card');
        openDesignCard.theme = this.theme;
        openDesignCard.localizedInformation = this.localizedInformation;
        openDesignCard.translations = this.translations;
        openDesignCard.settings = this.getSettings();
        openDesignCard.loggedIn = this.loggedIn;
        openDesignCard.enableTabs = this.enableTabs;
        openDesignCard.openDesignCodeState = this.openDesignCodeState;
        openDesignCard.savedDesigns = this.savedDesigns;
        openDesignCard.variant = this.variant;
        openDesignCard.helpMessage = this.helpMessage;
        openDesignCard.addEventListener('tabSelect', (event) => {
            const selectedTab = event.detail.selectedTab;
            if (this.selectedTab !== selectedTab) {
                openDesignCard.selectedTab = selectedTab;
                this.selectedTab = selectedTab;
                this.dispatchEvent(new CustomEvent('tabSelect', {
                    bubbles: true,
                    detail: { selectedTab }
                }));
            }
        });
        openDesignCard.addEventListener('designCodeInput', () => {
            const button = this.getFooterButton();
            if (button) {
                button.disabled = !this.isValidDesignCode();
            }
        });
        openDesignCard.addEventListener('openDesignCode', () => this.dispatchOpenDesignCodeEvent());
        openDesignCard.addEventListener('designSelect', (event) => {
            const designId = event.detail.designId;
            this.dispatchEvent(new CustomEvent('openSavedDesign', {
                bubbles: true,
                detail: { designId }
            }));
        });
        openDesignCard.addEventListener('resetOpenDesignCodeState', (event) => this.dispatchEvent(new CustomEvent('resetOpenDesignCodeState', {
            bubbles: true,
            detail: event.detail
        })));
        return openDesignCard;
    }
    /**
     * Recreates the sheet footer. This should be done when props (or events) that affect the footer are updated.
     * For example, different buttons should be shown depending on the props. It's easier to recreate the entire footer then to replace the individual button.
     */
    recreateSheetFooter() {
        if (this.sheetElement) {
            if (this.sheetFooterElement) {
                this.sheetElement.removeChild(this.sheetFooterElement);
                this.sheetFooterElement = null;
            }
            const footer = this.createSheetFooter();
            if (footer) {
                this.sheetElement.appendChild(footer);
                this.sheetFooterElement = footer;
            }
        }
    }
    createSheetFooter() {
        return this.selectedTab === OpenDesignTabEnum.designCode
            ? this.createDesignCodeSheetFooter()
            : this.createIkeaProfileSheetFooter();
    }
    createIkeaProfileSheetFooter() {
        if (!this.loggedIn) {
            const sheetFooterElement = this.createInternalElement('skapa-modal-footer');
            sheetFooterElement.slot = 'footer';
            const button = this.createInternalElement('skapa-button');
            button.slot = 'primary-button';
            button.variant = 'emphasised';
            button.a11yLabel = this.getTranslations().saveDesignToProfileLogInOrRegisterButtonLabel;
            button.textContent = this.getTranslations().saveDesignToProfileLogInOrRegisterButtonLabel;
            button.addEventListener('click', () => {
                this.dispatchEvent(new CustomEvent('logIn', { bubbles: true }));
            });
            sheetFooterElement.appendChild(button);
            return sheetFooterElement;
        }
        return null;
    }
    createDesignCodeSheetFooter() {
        const sheetFooterElement = this.createInternalElement('skapa-modal-footer');
        sheetFooterElement.slot = 'footer';
        const button = this.createInternalElement('skapa-button');
        const label = this.variant === OpenDesignVariantEnum.openDesign
            ? this.getTranslations().open
            : this.getTranslations().import;
        button.slot = 'primary-button';
        button.variant = 'primary';
        button.a11yLabel = label;
        button.textContent = label;
        button.disabled = !this.isValidDesignCode();
        button.loading = this.openSavedDesignState === OpenDesignStateEnum.loading;
        button.addEventListener('click', () => this.dispatchOpenDesignCodeEvent());
        sheetFooterElement.appendChild(button);
        return sheetFooterElement;
    }
    closePrompt() {
        if (this.promptElement) {
            this.dispatchEvent(new CustomEvent('promptClose', {
                bubbles: true,
                detail: {
                    visiblePrompt: null
                }
            }));
            this.promptElement.open = false;
            this.promptElement?.parentElement?.removeChild(this.promptElement);
            this.promptElement.removeEventListener('closerequest', this.handleCloseRequest.bind(this));
            this.promptElement.removeEventListener('closed', this.handleCloseRequest.bind(this));
            this.promptElement = null;
        }
    }
    openPrompt() {
        this.promptElement = this.createPrompt();
        const modalHeader = this.createModalHeader();
        const heading = this.createPromptHeading();
        const content = this.createPromptContent();
        const footer = this.createModalFooter();
        this.promptElement.appendChild(modalHeader);
        this.promptElement.appendChild(heading);
        this.promptElement.appendChild(content);
        this.promptElement.appendChild(footer);
        const parent = KeyboardInjectorFinder.findKeyboardInjectorElement() || document.body;
        parent.appendChild(this.promptElement);
        this.promptElement.open = true;
    }
    createPrompt() {
        const promptElement = this.createInternalElement('skapa-prompt');
        promptElement.setAttribute('aria-label', this.getTranslations().loginToContinue);
        promptElement.style.fontFamily = FontStyle.getFontFamily(this.theme);
        promptElement.style.zIndex = '500';
        promptElement.addEventListener('closerequest', this.handleCloseRequest.bind(this));
        promptElement.addEventListener('closed', this.handleCloseRequest.bind(this));
        return promptElement;
    }
    handleCloseRequest(e) {
        e.preventDefault();
        this.closePrompt();
    }
    createModalHeader() {
        const modalHeader = this.createInternalElement('skapa-modal-header');
        modalHeader.slot = 'header';
        const closeButton = document.createElement('span');
        closeButton.slot = 'closebutton-label';
        closeButton.innerText = this.getTranslations().modalAriaLabelButtonClose;
        modalHeader.appendChild(closeButton);
        return modalHeader;
    }
    createPromptHeading() {
        const heading = document.createElement('h2');
        heading.slot = 'title';
        heading.innerText = this.getTranslations().loginToContinue;
        heading.className = Text.classnames({ heading: { m: true } });
        const style = document.createElement('style');
        style.textContent = OpenDesignStyle.getStyle({ theme: this.theme });
        heading.appendChild(style);
        return heading;
    }
    createPromptContent() {
        const body = this.createInternalElement('skapa-inline-message');
        body.subtle = true;
        const bodySpan = document.createElement('span');
        bodySpan.slot = 'body';
        bodySpan.innerText = this.getTranslations().openDesignLogInMessage;
        bodySpan.className = Text.classnames({ body: { l: true } });
        const style = document.createElement('style');
        style.textContent = Text.CSS;
        bodySpan.appendChild(style);
        body.appendChild(bodySpan);
        return body;
    }
    createModalFooter() {
        const footer = this.createInternalElement('skapa-modal-footer');
        footer.slot = 'footer';
        footer.addEventListener('primary', () => this.dispatchEvent(new CustomEvent('logIn', { bubbles: true })));
        const footerButton = this.createInternalElement('skapa-button');
        footerButton.slot = 'primary-button';
        footerButton.variant = 'emphasised';
        footerButton.innerText = this.getTranslations().saveDesignToProfileLogInOrRegisterButtonLabel;
        footerButton.a11yLabel = this.getTranslations().saveDesignToProfileLogInOrRegisterButtonLabel;
        footer.appendChild(footerButton);
        return footer;
    }
    isValidDesignCode() {
        return this.getOpenDesignCodeInput()?.isValidVpcCode() ?? false;
    }
    getDesignCode() {
        return this.getOpenDesignCodeInput()?.getVpcCode() ?? '';
    }
    getOpenDesignCodeInput() {
        return (this.cardElement?.shadowRoot.querySelector(this.cardElement?.customElements.getInternalName('kompis-open-design-code-input')) ?? null);
    }
    dispatchOpenDesignCodeEvent() {
        this.dispatchEvent(new CustomEvent('openDesignCode', {
            bubbles: true,
            detail: { designCode: this.getDesignCode() }
        }));
    }
    getFooterButton() {
        return (this.sheetFooterElement?.querySelector(this.customElements.getInternalName('skapa-button')) ??
            null);
    }
}
__decorate([
    property({ type: Object, required: true })
], OpenDesign.prototype, "theme", void 0);
__decorate([
    property({ type: Object })
], OpenDesign.prototype, "localizedInformation", void 0);
__decorate([
    property({ type: Object })
], OpenDesign.prototype, "translations", void 0);
__decorate([
    property({ type: Object })
], OpenDesign.prototype, "settings", void 0);
__decorate([
    property({ type: String })
], OpenDesign.prototype, "visibleModal", void 0);
__decorate([
    property({ type: String })
], OpenDesign.prototype, "visiblePrompt", void 0);
__decorate([
    property({ type: Boolean })
], OpenDesign.prototype, "loggedIn", void 0);
__decorate([
    property({ type: Boolean })
], OpenDesign.prototype, "enableTabs", void 0);
__decorate([
    property({ type: String })
], OpenDesign.prototype, "openDesignCodeState", void 0);
__decorate([
    property({ type: Object })
], OpenDesign.prototype, "savedDesigns", void 0);
__decorate([
    property({ type: String })
], OpenDesign.prototype, "openSavedDesignState", void 0);
__decorate([
    property({ type: String })
], OpenDesign.prototype, "sheetAlignment", void 0);
__decorate([
    property({ type: Boolean })
], OpenDesign.prototype, "showBackButton", void 0);
__decorate([
    property({ type: String })
], OpenDesign.prototype, "variant", void 0);
__decorate([
    property({ type: String })
], OpenDesign.prototype, "helpMessage", void 0);
__decorate([
    property({ type: String })
], OpenDesign.prototype, "selectedTab", void 0);
//# sourceMappingURL=OpenDesign.js.map